import { Box, Tab, TabList, TabPanel, TabPanels, Tabs } from "@chakra-ui/react";
import { RpcError } from "grpc-web";
import { isEmpty } from "lodash";
import { FunctionComponent, ReactElement, useState } from "react";
import { generatePath, useSearchParams } from "react-router-dom";

// Components
import LayoutWrapper from "../../shared/layout";
import PaymentExportsTable from "./PaymentExportsTable";
import ApprovedClaimsIndexTable from "./index/ApprovedClaimsIndexTable";
import LinkableTableCell from "../../shared/DataTable/shared/LinkableTableCell";
import AttorneyActiveBadge from "../../shared/AttorneyActiveBadge/AttorneyActiveBadge";

// Hooks
import useClaimsToast from "../../hooks/useToast";
import { IParams, getCacheKeyObject } from "../../shared/DataTable/hooks/useSearchParams";

// Utils
import { RouterPathMapping } from "../../Router";
import { getClaimsList } from "../../utils/api/claims";
import { dummyPaginatedApiResponse } from "../../utils/consts";
import { convertUnixTimestampToLocalDate } from "../../utils/helpers";
import { DataTableColumn, KVP, PaginatedApiResponse, PaginationResponse, SortDirection } from "../../utils/types";

// Protos
import { Claim as ApiClaim, ClaimPerson, ClaimsListResponse } from "../../utils/api/protos/messages/claims.message_pb";
import { PaginationResponse as ApiPaginationResponse } from "../../utils/api/protos/messages/core.message_pb";

interface PaymentsPageProps {}

/**
 * Define tabs on the page
 */
enum PaymentsTabs {
	Exports,
	ApprovedClaims
}

const defaultApprovedClaimsParams: Partial<IParams> = {
	sortDirection: SortDirection.Desc,
	sortField: "id",
	searchField: "status",
	searchString: "Approved"
};

/**
 * Handles the Payments page with Exports and Approved Claims tabs
 * Shows different data depending on which tab is active
 * Each individual tab is rendered as a child component
 * @param props typeof: PaymentsPageProps
 * @returns ReactElement
 */
const PaymentsPage: FunctionComponent<PaymentsPageProps> = (props: PaymentsPageProps): ReactElement => {
	const [activeTab, setActiveTab] = useState<PaymentsTabs>(PaymentsTabs.Exports);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [approvedClaimsData, setApprovedClaimsData] = useState<PaginatedApiResponse>(dummyPaginatedApiResponse);
	const [approvedClaimsCacheKey, setApprovedClaimsCacheKey] = useState<string>("");
	const [searchParams] = useSearchParams();
	const toast = useClaimsToast();

	/**
	 * Define columns for Approved Claims table
	 */
	const approvedClaimsColumns: Array<DataTableColumn> = [
		{
			header: "Id",
			id: "id",
			filterable: true,
			minWidth: "10ch",
			isHidden: false,
			isSelectable: true,
			isSortable: true,
			allData: true,
			valueMap: (claim: KVP) => (
				<LinkableTableCell
					To={generatePath(RouterPathMapping.ClaimDetail, { id: String(claim.id) })}
					Contents={claim.id as string | number}
				/>
			)
		},
		{
			header: "Claims Analyst",
			id: "assigned_analyst_name",
			filterable: false,
			minWidth: "15ch",
			isHidden: false,
			isSortable: false
		},
		{
			header: "PASS Case ID",
			id: "case_id",
			filterable: true,
			minWidth: "15ch",
			isHidden: false,
			isSortable: true
		},
		{
			header: "LAMP Case ID",
			id: "lamp_case_id",
			filterable: false,
			minWidth: "15ch",
			isHidden: false,
			isSortable: false
		},
		{
			header: "Attorney ID",
			id: "attorney_id",
			filterable: false,
			minWidth: "12ch",
			isHidden: false,
			isSortable: false
		},
		{
			header: "Attorney Name",
			id: "attorney_name",
			filterable: false,
			minWidth: "15ch",
			isHidden: false,
			isSortable: false
		},
		{
			header: "Active",
			id: "attorney_active",
			filterable: false,
			minWidth: "9ch",
			isHidden: false,
			isSortable: false,
			allData: true,
			valueMap: (claim: KVP) => <AttorneyActiveBadge active={claim["attorney_active"] === 1} showInactive />
		},
		{
			header: "Member ID",
			id: "member_id",
			filterable: false,
			minWidth: "12ch",
			isHidden: false,
			isSortable: false
		},
		{
			header: "Member Name",
			id: "member_name",
			filterable: false,
			minWidth: "15ch",
			isHidden: false,
			isSortable: false
		},
		{
			header: "Started",
			id: "started_at",
			filterable: false,
			isDateTime: true,
			minWidth: "10ch",
			isHidden: false,
			isSortable: false
		},
		{
			header: "Last Updated",
			id: "updated_at",
			filterable: true,
			isDateTime: true,
			minWidth: "10ch",
			isHidden: false,
			isSortable: true
		}
	];

	const handleChangedTab = (index: number): void => {
		setActiveTab(index);
	};

	/**
	 * Helper function to fetch approved claims data
	 * Always filters by Approved status regardless of user input
	 * @param cacheKey The cacheKey of the currently active table
	 */
	const handleFetchApprovedClaimsData = (cacheKey: string): void => {
		if (!isEmpty(cacheKey)) {
			setIsLoading(true);
			if (cacheKey !== approvedClaimsCacheKey) setApprovedClaimsCacheKey(cacheKey);
			const params = getCacheKeyObject(cacheKey);
			// Force status filter to always be "Approved"
			params.searchField = "status";
			params.searchString = "Approved";
			getClaimsList(params)
				.then((response: ClaimsListResponse) => {
					// Build response data
					const returnedClaimsData: Array<{ [key: string]: string | number }> = response
						.getClaimList()
						.map((claim: ApiClaim) => {
							const atty: ClaimPerson | undefined = claim.getAtty();
							const member: ClaimPerson | undefined = claim.getMember();
							const assignedAnalyst: ClaimPerson | undefined = claim.getAssignedAnalyst();

							return {
								id: claim.getId(),
								case_id: claim.getCaseId(),
								lamp_case_id: claim.getLampCaseId() || "",
								attorney_id: atty?.getId() || "",
								attorney_name: atty?.getName() || "",
								attorney_active: claim.getAttyActive() ? 1 : 0,
								member_id: member?.getId() || "",
								member_name: member?.getName() || "",
								assigned_analyst_name: assignedAnalyst?.getName() || "",
								started_at: convertUnixTimestampToLocalDate(claim.getCreatedAt()).toLocaleString(),
								updated_at: convertUnixTimestampToLocalDate(claim.getUpdatedAt()).toLocaleString()
							};
						});
					const foundPaginationData: ApiPaginationResponse | undefined = response.getPaginationResponse();
					if (foundPaginationData) {
						const returnedPaginationData: PaginationResponse = {
							current_page: foundPaginationData.getCurrentPage(),
							first_page: foundPaginationData.getFirstPage(),
							last_page: foundPaginationData.getLastPage(),
							perPage: foundPaginationData.getLimit(),
							total: foundPaginationData.getTotal()
						};

						setApprovedClaimsData({
							data: returnedClaimsData,
							meta: returnedPaginationData
						});
					} else {
						toast("Error", "There was a problem deciphering the pagination metadata", "error");
						console.error("Attempting to parse pagination metadata that does not exist!!");
					}
				})
				.catch((err) => {
					toast("Error", (err as RpcError).message, "error");
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	};

	return (
		<>
			<LayoutWrapper>
				<Box data-testid="payments-wrapper">
					<Tabs
						colorScheme="orange"
						variant="enclosed-colored"
						margin="20px 10px"
						isLazy={true}
						index={activeTab}
						onChange={(index: number) => handleChangedTab(index)}>
						<Box display="flex" borderColor="transparent" marginLeft="1px">
							<TabList marginLeft="15px" position="relative">
								<Tab
									borderTopWidth="0px"
									backgroundColor="gray.200"
									fontWeight="bold"
									fontSize="19px"
									_selected={{ bg: "white" }}>
									Exports
								</Tab>
								<Tab
									borderTopWidth="0px"
									backgroundColor="gray.200"
									fontWeight="bold"
									fontSize="19px"
									marginLeft="4px"
									_selected={{ bg: "white" }}>
									Approved Claims
								</Tab>
								{/* Filler background tab */}
								<Tab
									position="absolute"
									backgroundColor="#e9e9e9"
									zIndex="-1"
									left="0"
									right="0"
									bottom="0"
									top="0"
									boxShadow="1px 2px 9px 1px gainsboro"
								/>
							</TabList>
						</Box>
						<TabPanels>
							<TabPanel pt="0">{activeTab === PaymentsTabs.Exports && <PaymentExportsTable />}</TabPanel>
							<TabPanel pt="0">
								{activeTab === PaymentsTabs.ApprovedClaims && (
									<ApprovedClaimsIndexTable
										claimsData={approvedClaimsData}
										columns={approvedClaimsColumns}
										searchParams={searchParams}
										handleFetchClaims={handleFetchApprovedClaimsData}
										handleManualRefetchTrigger={() => handleFetchApprovedClaimsData(approvedClaimsCacheKey)}
										defaultParams={defaultApprovedClaimsParams}
										isLoading={isLoading}
									/>
								)}
							</TabPanel>
						</TabPanels>
					</Tabs>
				</Box>
			</LayoutWrapper>
		</>
	);
};

export default PaymentsPage;
